import { useState, useEffect, useRef, useCallback } from 'react'
import { Users, ChevronDown, ChevronUp, Loader2 } from 'lucide-react'
import { FilterState } from '@/components/filters/types'

interface PreviewUser {
  id: number
  name: string | null
  email: string | null
}

interface AudiencePreviewProps {
  audienceRules: FilterState
}

function getCsrfToken(): string {
  return document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
}

function AudienceCountLabel({ count, hasFilters }: { count: number | null; hasFilters: boolean }) {
  if (count === null) return <>Carregando...</>
  if (!hasFilters) return <>Todos os usuários</>
  return (
    <span>
      <span className="font-semibold">{count}</span>{' '}
      {count === 1 ? 'pessoa' : 'pessoas'}
    </span>
  )
}

export default function AudiencePreview({ audienceRules }: AudiencePreviewProps) {
  const [count, setCount] = useState<number | null>(null)
  const [users, setUsers] = useState<PreviewUser[]>([])
  const [expanded, setExpanded] = useState(false)
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(false)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const perPage = 10

  const fetchPreview = useCallback(async (pageNum: number, append: boolean) => {
    setLoading(true)
    try {
      const response = await fetch('/outbound_messages/preview_audience', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': getCsrfToken(),
        },
        body: JSON.stringify({
          audience_rules: audienceRules,
          page: pageNum,
          per_page: perPage,
        }),
      })

      if (response.ok) {
        const data = await response.json()
        setCount(data.count)
        if (append) {
          setUsers((prev) => [...prev, ...data.users])
        } else {
          setUsers(data.users)
        }
        setHasMore(data.users.length === perPage && pageNum * perPage < data.count)
      }
    } catch {
      // Silently fail on network errors
    } finally {
      setLoading(false)
    }
  }, [audienceRules])

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)

    debounceRef.current = setTimeout(() => {
      setPage(1)
      setExpanded(false)
      fetchPreview(1, false)
    }, 500)

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [audienceRules, fetchPreview])

  const loadMore = () => {
    const nextPage = page + 1
    setPage(nextPage)
    fetchPreview(nextPage, true)
  }

  const hasFilters = audienceRules.filters.length > 0

  return (
    <div className="mt-3 border border-gray-200 rounded-lg bg-white">
      <div className="flex items-center justify-between px-4 py-2.5">
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-gray-500" />
          {loading && count === null ? (
            <Loader2 className="w-4 h-4 text-gray-400 animate-spin" />
          ) : (
            <span className="text-sm text-gray-700">
              <AudienceCountLabel count={count} hasFilters={hasFilters} />
            </span>
          )}
          {loading && count !== null && (
            <Loader2 className="w-3.5 h-3.5 text-gray-400 animate-spin" />
          )}
        </div>

        {count !== null && count > 0 && (
          <button
            type="button"
            onClick={() => setExpanded(!expanded)}
            className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700"
          >
            {expanded ? (
              <>
                Ocultar <ChevronUp className="w-3.5 h-3.5" />
              </>
            ) : (
              <>
                Ver público <ChevronDown className="w-3.5 h-3.5" />
              </>
            )}
          </button>
        )}
      </div>

      {expanded && users.length > 0 && (
        <div className="border-t border-gray-100">
          <div className="divide-y divide-gray-50">
            {users.map((user) => (
              <div key={user.id} className="px-4 py-2 flex items-center gap-3">
                <div className="w-7 h-7 rounded-full bg-gray-200 flex items-center justify-center text-xs font-medium text-gray-600">
                  {(user.name || user.email || '?')[0].toUpperCase()}
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-800 truncate">
                    {user.name || 'Sem nome'}
                  </p>
                  {user.email && (
                    <p className="text-xs text-gray-500 truncate">{user.email}</p>
                  )}
                </div>
              </div>
            ))}
          </div>

          {hasMore && (
            <div className="px-4 py-2 border-t border-gray-100">
              <button
                type="button"
                onClick={loadMore}
                disabled={loading}
                className="text-sm text-blue-600 hover:text-blue-700 disabled:opacity-50"
              >
                {loading ? 'Carregando...' : 'Carregar mais'}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
